import { optionalQueryParams } from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  JsonValue as AdminV2JsonValue,
  AdminV2LearningAssignment,
  AdminV2LearningAssignmentsResponse,
  AdminV2LearningAssignmentStatus,
  AdminV2LearningBusinessScoreDashboardResponse,
  AdminV2LearningContentDetail,
  AdminV2LearningContentsResponse,
  AdminV2LearningContentStatus,
  AdminV2LearningContentType,
  AdminV2LearningPathwayDetail,
  AdminV2LearningPathwaysResponse,
  AdminV2LearningPathwayStatus,
  AdminV2LearningPeopleScoreDashboardResponse,
  AdminV2LearningProgress,
  AdminV2LearningProgressResponse,
  AdminV2LearningProgressStatus,
  AdminV2LearningRecomputeScoreResponse,
  AdminV2LearningScoreExplainabilityResponse,
  AdminV2LearningScoreRule,
  AdminV2LearningScoreRuleVersionsResponse,
  AdminV2LearningScoreTimelineResponse,
} from "@/types/adminV2";

export const buildAdminV2LearningEndpoints = (builder: ApiEndpointBuilder) => ({
  getAdminV2LearningContents: builder.query<
    AdminV2LearningContentsResponse,
    {
      organisationId?: string;
      q?: string;
      status?: AdminV2LearningContentStatus;
      contentType?: AdminV2LearningContentType;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/learning/content",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2LearningContent", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2LearningContent: builder.query<
    AdminV2LearningContentDetail,
    { contentId: string }
  >({
    query: ({ contentId }) => ({
      url: `/admin/v2/learning/content/${contentId}`,
    }),
    providesTags: (_result, _error, { contentId }) => [
      { type: "AdminV2LearningContent", id: contentId },
    ],
    keepUnusedDataFor: 10,
  }),

  createAdminV2LearningContent: builder.mutation<
    AdminV2LearningContentDetail,
    {
      organisationId: string;
      title: string;
      contentKey?: string;
      summary?: string;
      contentType?: AdminV2LearningContentType;
      body?: AdminV2JsonValue;
      settings?: AdminV2JsonValue;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/learning/content",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2LearningContent", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2LearningContent: builder.mutation<
    AdminV2LearningContentDetail,
    {
      contentId: string;
      body: {
        contentKey?: string;
        title?: string;
        summary?: string | null;
        contentType?: AdminV2LearningContentType;
        body?: AdminV2JsonValue;
        settings?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ contentId, body }) => ({
      url: `/admin/v2/learning/content/${contentId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { contentId }) => [
      { type: "AdminV2LearningContent", id: "LIST" },
      { type: "AdminV2LearningContent", id: contentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2LearningContent: builder.mutation<
    AdminV2LearningContentDetail,
    { contentId: string }
  >({
    query: ({ contentId }) => ({
      url: `/admin/v2/learning/content/${contentId}/publish`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { contentId }) => [
      { type: "AdminV2LearningContent", id: "LIST" },
      { type: "AdminV2LearningContent", id: contentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  archiveAdminV2LearningContent: builder.mutation<
    AdminV2LearningContentDetail,
    { contentId: string }
  >({
    query: ({ contentId }) => ({
      url: `/admin/v2/learning/content/${contentId}/archive`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { contentId }) => [
      { type: "AdminV2LearningContent", id: "LIST" },
      { type: "AdminV2LearningContent", id: contentId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2LearningPathways: builder.query<
    AdminV2LearningPathwaysResponse,
    {
      organisationId?: string;
      q?: string;
      status?: AdminV2LearningPathwayStatus;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/learning/pathways",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2LearningPathways", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2LearningPathway: builder.query<
    AdminV2LearningPathwayDetail,
    { pathwayId: string }
  >({
    query: ({ pathwayId }) => ({
      url: `/admin/v2/learning/pathways/${pathwayId}`,
    }),
    providesTags: (_result, _error, { pathwayId }) => [
      { type: "AdminV2LearningPathways", id: pathwayId },
    ],
    keepUnusedDataFor: 10,
  }),

  createAdminV2LearningPathway: builder.mutation<
    AdminV2LearningPathwayDetail,
    {
      organisationId: string;
      name: string;
      pathwayKey?: string;
      description?: string;
      schema?: AdminV2JsonValue;
      settings?: AdminV2JsonValue;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/learning/pathways",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2LearningPathways", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  updateAdminV2LearningPathway: builder.mutation<
    AdminV2LearningPathwayDetail,
    {
      pathwayId: string;
      body: {
        pathwayKey?: string;
        name?: string;
        description?: string | null;
        schema?: AdminV2JsonValue;
        settings?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ pathwayId, body }) => ({
      url: `/admin/v2/learning/pathways/${pathwayId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { pathwayId }) => [
      { type: "AdminV2LearningPathways", id: "LIST" },
      { type: "AdminV2LearningPathways", id: pathwayId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2LearningPathway: builder.mutation<
    AdminV2LearningPathwayDetail,
    { pathwayId: string }
  >({
    query: ({ pathwayId }) => ({
      url: `/admin/v2/learning/pathways/${pathwayId}/publish`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { pathwayId }) => [
      { type: "AdminV2LearningPathways", id: "LIST" },
      { type: "AdminV2LearningPathways", id: pathwayId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  archiveAdminV2LearningPathway: builder.mutation<
    AdminV2LearningPathwayDetail,
    { pathwayId: string }
  >({
    query: ({ pathwayId }) => ({
      url: `/admin/v2/learning/pathways/${pathwayId}/archive`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { pathwayId }) => [
      { type: "AdminV2LearningPathways", id: "LIST" },
      { type: "AdminV2LearningPathways", id: pathwayId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  assignAdminV2LearningPathway: builder.mutation<
    {
      assignment: AdminV2LearningAssignment;
      progress: AdminV2LearningProgress[];
    },
    {
      pathwayId: string;
      body: {
        organisationId: string;
        personId?: string;
        businessId?: string;
        dueAt?: string;
        metadata?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ pathwayId, body }) => ({
      url: `/admin/v2/learning/pathways/${pathwayId}/assign`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { pathwayId }) => [
      { type: "AdminV2LearningPathways", id: "LIST" },
      { type: "AdminV2LearningPathways", id: pathwayId },
      { type: "AdminV2LearningAssignments", id: "LIST" },
      { type: "AdminV2LearningProgress", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2LearningAssignments: builder.query<
    AdminV2LearningAssignmentsResponse,
    {
      organisationId?: string;
      pathwayId?: string;
      personId?: string;
      businessId?: string;
      status?: AdminV2LearningAssignmentStatus;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/learning/assignments",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2LearningAssignments", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  getAdminV2LearningProgress: builder.query<
    AdminV2LearningProgressResponse,
    {
      organisationId?: string;
      assignmentId?: string;
      pathwayId?: string;
      contentId?: string;
      personId?: string;
      businessId?: string;
      status?: AdminV2LearningProgressStatus;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/learning/progress",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2LearningProgress", id: "LIST" }],
    keepUnusedDataFor: 10,
  }),

  updateAdminV2LearningProgress: builder.mutation<
    {
      progress: AdminV2LearningProgress;
      assignment: AdminV2LearningAssignment;
      score: AdminV2LearningRecomputeScoreResponse;
    },
    {
      organisationId: string;
      assignmentId: string;
      contentId: string;
      status: AdminV2LearningProgressStatus;
      progressPercent?: number;
      source?: string;
      metadata?: AdminV2JsonValue;
      completedAt?: string;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/learning/progress",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2LearningProgress", id: "LIST" },
      { type: "AdminV2LearningAssignments", id: "LIST" },
      { type: "AdminV2LearningScores", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2LearningScoreRuleVersions: builder.query<
    AdminV2LearningScoreRuleVersionsResponse,
    {
      organisationId: string;
      scoreModelKey?: string;
      limit?: number;
      offset?: number;
    }
  >({
    query: (params) => ({
      url: "/admin/v2/learning/scores/rules",
      params,
    }),
    providesTags: [{ type: "AdminV2LearningScoreRules", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  createAdminV2LearningScoreRuleVersion: builder.mutation<
    {
      ruleVersion: AdminV2LearningScoreRuleVersionsResponse["ruleVersions"][number];
    },
    {
      organisationId: string;
      scoreModelKey?: string;
      rules: AdminV2LearningScoreRule[];
      notes?: string;
      publish?: boolean;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/learning/scores/rules",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2LearningScoreRules", id: "LIST" },
      { type: "AdminV2LearningScores", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2LearningScoreRuleVersion: builder.mutation<
    {
      ruleVersion: AdminV2LearningScoreRuleVersionsResponse["ruleVersions"][number];
    },
    { ruleVersionId: string; organisationId: string }
  >({
    query: ({ ruleVersionId, organisationId }) => ({
      url: `/admin/v2/learning/scores/rules/${ruleVersionId}/publish`,
      method: "POST",
      params: {
        organisationId,
      },
      data: {},
    }),
    invalidatesTags: [
      { type: "AdminV2LearningScoreRules", id: "LIST" },
      { type: "AdminV2LearningScores", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2LearningPeopleScoreDashboard: builder.query<
    AdminV2LearningPeopleScoreDashboardResponse,
    {
      organisationId?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/learning/scores/dashboard/people",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
    keepUnusedDataFor: 10,
  }),

  getAdminV2LearningBusinessScoreDashboard: builder.query<
    AdminV2LearningBusinessScoreDashboardResponse,
    {
      organisationId?: string;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/learning/scores/dashboard/businesses",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
    keepUnusedDataFor: 10,
  }),

  getAdminV2LearningScoreTimeline: builder.query<
    AdminV2LearningScoreTimelineResponse,
    {
      organisationId: string;
      personId?: string;
      businessId?: string;
      limit?: number;
      offset?: number;
    }
  >({
    query: (params) => ({
      url: "/admin/v2/learning/scores/timeline",
      params,
    }),
    providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
    keepUnusedDataFor: 10,
  }),

  getAdminV2LearningScoreExplainability: builder.query<
    AdminV2LearningScoreExplainabilityResponse,
    {
      organisationId: string;
      scoreSnapshotId?: string;
      personId?: string;
      businessId?: string;
    }
  >({
    query: (params) => ({
      url: "/admin/v2/learning/scores/explainability",
      params,
    }),
    providesTags: [{ type: "AdminV2LearningScores", id: "LIST" }],
    keepUnusedDataFor: 10,
  }),

  recomputeAdminV2LearningScore: builder.mutation<
    AdminV2LearningRecomputeScoreResponse,
    {
      organisationId: string;
      personId?: string;
      businessId?: string;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/learning/scores/recompute",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2LearningScores", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
